import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format timestamp in IST
const formatISTDateTime = (timestamp) => {
  const date = new Date(timestamp);
  // Convert to IST (UTC+5:30)
  const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
  
  const day = istDate.getUTCDate().toString().padStart(2, '0');
  const month = (istDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = istDate.getUTCFullYear();
  const hours = istDate.getUTCHours().toString().padStart(2, '0');
  const minutes = istDate.getUTCMinutes().toString().padStart(2, '0');
  const seconds = istDate.getUTCSeconds().toString().padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} IST`;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("valuation-leads");
  const [valuationLeads, setValuationLeads] = useState([]);
  const [propertyLeads, setPropertyLeads] = useState([]);
  const [buyerStrategyLeads, setBuyerStrategyLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [viewingReport, setViewingReport] = useState(null); // For full report modal
  const [propertyForm, setPropertyForm] = useState({
    location: "",
    society_name: "",
    bhk: "",
    area_sqft: "",
    floor: "",
    price: "",
    description: "",
    contact_whatsapp: "",
    images: [],
    video_url: ""
  });

  React.useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [valLeads, propLeads, buyerLeads, props] = await Promise.all([
        axios.get(`${API}/admin/valuation-leads`, { headers }),
        axios.get(`${API}/admin/property-leads`, { headers }),
        axios.get(`${API}/admin/buyer-strategy-leads`, { headers }),
        axios.get(`${API}/properties`)
      ]);

      setValuationLeads(valLeads.data);
      setPropertyLeads(propLeads.data);
      setBuyerStrategyLeads(buyerLeads.data);
      setProperties(props.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUsername");
    navigate("/admin");
  };

  const handleEditProperty = (property) => {
    setEditingProperty(property);
    setPropertyForm({
      location: property.location,
      society_name: property.society_name,
      bhk: property.bhk,
      area_sqft: property.area_sqft.toString(),
      floor: property.floor,
      price: property.price.toString(),
      description: property.description,
      contact_whatsapp: property.contact_whatsapp,
      images: property.images || [],
      video_url: property.video_url || ""
    });
    setShowAddProperty(true);
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");

    try {
      if (editingProperty) {
        // Update existing property - preserve original ID and timestamp
        const updateData = {
          id: editingProperty.id,
          location: propertyForm.location,
          society_name: propertyForm.society_name,
          bhk: propertyForm.bhk,
          area_sqft: parseFloat(propertyForm.area_sqft),
          floor: propertyForm.floor,
          price: parseFloat(propertyForm.price),
          description: propertyForm.description,
          contact_whatsapp: propertyForm.contact_whatsapp,
          images: propertyForm.images,
          video_url: propertyForm.video_url,
          timestamp: editingProperty.timestamp
        };

        await axios.put(
          `${API}/admin/properties/${editingProperty.id}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert("Property updated successfully!");
      } else {
        // Add new property
        await axios.post(
          `${API}/admin/properties`,
          {
            ...propertyForm,
            area_sqft: parseFloat(propertyForm.area_sqft),
            price: parseFloat(propertyForm.price)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert("Property added successfully!");
      }

      setShowAddProperty(false);
      setEditingProperty(null);
      setPropertyForm({
        location: "",
        society_name: "",
        bhk: "",
        area_sqft: "",
        floor: "",
        price: "",
        description: "",
        contact_whatsapp: "",
        images: [],
        video_url: ""
      });
      fetchData();
    } catch (error) {
      console.error("Error saving property:", error);
      console.error("Error details:", error.response?.data);
      alert(`Error saving property: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;

    const token = localStorage.getItem("adminToken");

    try {
      await axios.delete(`${API}/admin/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert("Property deleted successfully!");
      fetchData();
    } catch (error) {
      console.error("Error deleting property:", error);
      alert("Error deleting property");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert("Please upload an image file");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/admin/upload-image`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setPropertyForm({
          ...propertyForm,
          images: [...propertyForm.images, response.data.image_url]
        });
      }
    } catch (error) {
      console.error("Image upload error:", error);
      alert(error.response?.data?.detail || "Failed to upload image");
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10MB max for MongoDB compatibility)
    if (file.size > 10 * 1024 * 1024) {
      alert("Video file size must be less than 10MB due to storage limitations.\n\nTip: You can compress your video using online tools before uploading.");
      e.target.value = ''; // Reset file input
      return;
    }

    // Validate file type
    if (!file.type.startsWith('video/')) {
      alert("Please upload a video file");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const formData = new FormData();
      formData.append('file', file);

      // Show uploading message
      const uploadingMsg = document.createElement('div');
      uploadingMsg.textContent = 'Uploading video...';
      uploadingMsg.style.cssText = 'position:fixed;top:20px;right:20px;background:#2563eb;color:white;padding:12px 24px;border-radius:8px;z-index:9999';
      document.body.appendChild(uploadingMsg);

      const response = await axios.post(
        `${API}/admin/upload-video`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      document.body.removeChild(uploadingMsg);

      if (response.data.success) {
        setPropertyForm({
          ...propertyForm,
          video_url: response.data.video_url
        });
        alert("Video uploaded successfully!");
      }
    } catch (error) {
      console.error("Video upload error:", error);
      const errorMsg = error.response?.data?.detail || "Failed to upload video. The file might be too large.";
      alert(errorMsg);
      e.target.value = ''; // Reset file input
    }
  };

  // Export functions
  const exportToExcel = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const exportToCSV = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${filename}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportValuationLeads = (format) => {
    const exportData = valuationLeads.map(lead => ({
      Name: lead.name,
      Mobile: lead.mobile,
      Email: lead.email,
      Location: lead.valuation_result?.property_details?.location || 'N/A',
      Property_Type: lead.valuation_result?.property_details?.bhk || 'N/A',
      Area_SqFt: lead.valuation_result?.property_details?.area_sqft || 'N/A',
      Market_Value_Min: lead.valuation_result?.estimated_market_value_min || 0,
      Market_Value_Max: lead.valuation_result?.estimated_market_value_max || 0,
      Quick_Sale_Price: lead.valuation_result?.quick_sale_price || 0,
      Investor_Safe_Buy_Price: lead.valuation_result?.investor_safe_buy_price || 0,
      Price_Per_SqFt: lead.valuation_result?.price_per_sqft || 0,
      Demand_Score: lead.valuation_result?.demand_score || 'N/A',
      Timestamp: formatISTDateTime(lead.timestamp)
    }));

    if (format === "excel") {
      exportToExcel(exportData, `valuation_leads_${new Date().toISOString().split('T')[0]}`);
    } else {
      exportToCSV(exportData, `valuation_leads_${new Date().toISOString().split('T')[0]}`);
    }
  };

  const handleExportPropertyLeads = (format) => {
    const exportData = propertyLeads.map(lead => ({
      Name: lead.name,
      Phone: lead.phone,
      Email: lead.email,
      Property_ID: lead.property_id,
      Message: lead.message || "N/A",
      Timestamp: formatISTDateTime(lead.timestamp)
    }));

    if (format === "excel") {
      exportToExcel(exportData, `property_leads_${new Date().toISOString().split('T')[0]}`);
    } else {
      exportToCSV(exportData, `property_leads_${new Date().toISOString().split('T')[0]}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-semibold">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="dashboard-title">
              Admin Dashboard
            </h1>
            <p className="text-gray-600">Welcome, {localStorage.getItem("adminUsername")}</p>
          </div>
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition"
            data-testid="logout-btn"
          >
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-8">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab("valuation-leads")}
              className={`flex-1 py-4 px-6 font-semibold transition ${
                activeTab === "valuation-leads"
                  ? "border-b-4 border-brand-500 text-brand-500"
                  : "text-gray-600 hover:text-brand-500"
              }`}
              data-testid="tab-valuation-leads"
            >
              Valuation Leads ({valuationLeads.length})
            </button>
            <button
              onClick={() => setActiveTab("property-leads")}
              className={`flex-1 py-4 px-6 font-semibold transition ${
                activeTab === "property-leads"
                  ? "border-b-4 border-brand-500 text-brand-500"
                  : "text-gray-600 hover:text-brand-500"
              }`}
              data-testid="tab-property-leads"
            >
              Buy Property Leads ({propertyLeads.length})
            </button>
            <button
              onClick={() => setActiveTab("buyer-strategy-leads")}
              className={`flex-1 py-4 px-6 font-semibold transition ${
                activeTab === "buyer-strategy-leads"
                  ? "border-b-4 border-brand-500 text-brand-500"
                  : "text-gray-600 hover:text-brand-500"
              }`}
            >
              Buyer Strategy ({buyerStrategyLeads.length})
            </button>
            <button
              onClick={() => setActiveTab("manage-properties")}
              className={`flex-1 py-4 px-6 font-semibold transition ${
                activeTab === "manage-properties"
                  ? "border-b-4 border-brand-500 text-brand-500"
                  : "text-gray-600 hover:text-brand-500"
              }`}
              data-testid="tab-manage-properties"
            >
              Manage Properties ({properties.length})
            </button>
          </div>

          <div className="p-6">
            {/* Valuation Leads Tab */}
            {activeTab === "valuation-leads" && (
              <div data-testid="valuation-leads-content">
                {/* Export Buttons */}
                <div className="mb-4 flex gap-3 justify-end">
                  <button
                    onClick={() => handleExportValuationLeads("excel")}
                    className="bg-brand-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-500 transition flex items-center gap-2"
                    disabled={valuationLeads.length === 0}
                  >
                    <span>📊</span> Export to Excel
                  </button>
                  <button
                    onClick={() => handleExportValuationLeads("csv")}
                    className="bg-brand-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-600 transition flex items-center gap-2"
                    disabled={valuationLeads.length === 0}
                  >
                    <span>📄</span> Export to CSV
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200 bg-gray-50">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Contact</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Location</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Property</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Market Value</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Quick Sale</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 bg-purple-50">Investor Price</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Demand Score</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Date & Time (IST)</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {valuationLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-gray-100 hover:bg-brand-50 transition">
                        <td className="py-3 px-4 font-medium">{lead.name}</td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            <div>{lead.mobile}</div>
                            <div className="text-gray-500">{lead.email}</div>
                          </div>
                        </td>
                        <td className="py-3 px-4">{lead.valuation_result?.property_details?.location || 'N/A'}</td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            <div>{lead.valuation_result?.property_details?.bhk || 'N/A'}</div>
                            <div className="text-gray-500">{lead.valuation_result?.property_details?.area_sqft || 'N/A'} sq ft</div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-semibold text-brand-500">
                            ₹{lead.valuation_result?.estimated_market_value_min || 0}L-{lead.valuation_result?.estimated_market_value_max || 0}L
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-semibold text-brand-500">
                            ₹{lead.valuation_result?.quick_sale_price || 0}L
                          </span>
                        </td>
                        <td className="py-3 px-4 bg-purple-50">
                          <span className="font-bold text-purple-600">
                            ₹{lead.valuation_result?.investor_safe_buy_price || 0}L
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            lead.valuation_result?.demand_score === 'High' ? 'bg-brand-100 text-brand-500' :
                            lead.valuation_result?.demand_score === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {lead.valuation_result?.demand_score || 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatISTDateTime(lead.timestamp)}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => setViewingReport(lead)}
                            className="bg-brand-500 text-white px-4 py-2 rounded-lg hover:bg-brand-600 transition text-sm font-semibold"
                          >
                            📋 View Full Report
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {/* Property Leads Tab */}
            {activeTab === "property-leads" && (
              <div data-testid="property-leads-content">
                {/* Export Buttons */}
                <div className="mb-4 flex gap-3 justify-end">
                  <button
                    onClick={() => handleExportPropertyLeads("excel")}
                    className="bg-brand-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-500 transition flex items-center gap-2"
                    disabled={propertyLeads.length === 0}
                  >
                    <span>📊</span> Export to Excel
                  </button>
                  <button
                    onClick={() => handleExportPropertyLeads("csv")}
                    className="bg-brand-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-600 transition flex items-center gap-2"
                    disabled={propertyLeads.length === 0}
                  >
                    <span>📄</span> Export to CSV
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Mobile</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Message</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Date & Time (IST)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {propertyLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">{lead.name}</td>
                        <td className="py-3 px-4">{lead.mobile}</td>
                        <td className="py-3 px-4">{lead.email}</td>
                        <td className="py-3 px-4">{lead.message || "-"}</td>
                        <td className="py-3 px-4">{formatISTDateTime(lead.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}


            {/* Buyer Strategy Leads Tab */}
            {activeTab === "buyer-strategy-leads" && (
              <div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Contact</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Purpose</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Budget</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Recommended</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">ROI</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Date & Time (IST)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buyerStrategyLeads.map((lead) => (
                        <tr key={lead.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-semibold">{lead.name}</td>
                          <td className="py-3 px-4">
                            <div>{lead.mobile}</div>
                            <div className="text-sm text-gray-600">{lead.email}</div>
                          </td>
                          <td className="py-3 px-4">
                            {lead.strategy?.inputs?.buying_purpose || '-'}
                          </td>
                          <td className="py-3 px-4">
                            {lead.strategy?.inputs?.budget || '-'}
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-semibold">{lead.strategy?.recommended_configuration}</div>
                            <div className="text-sm text-gray-600">{lead.strategy?.recommended_type}</div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="bg-brand-100 text-brand-600 px-2 py-1 rounded text-sm font-semibold">
                              {lead.strategy?.target_roi}
                            </span>
                          </td>
                          <td className="py-3 px-4">{formatISTDateTime(lead.timestamp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Manage Properties Tab */}
            {activeTab === "manage-properties" && (
              <div data-testid="manage-properties-content">
                <button
                  onClick={() => setShowAddProperty(true)}
                  className="bg-brand-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-600 transition mb-6"
                  data-testid="add-property-btn"
                >
                  + Add New Property
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {properties.map((property) => (
                    <div key={property.id} className="border-2 border-gray-200 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{property.society_name}</h3>
                          <p className="text-gray-600">{property.location}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditProperty(property)}
                            className="bg-brand-500 text-white px-3 py-1 rounded-lg text-sm font-semibold hover:bg-brand-600"
                            data-testid="edit-property-btn"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteProperty(property.id)}
                            className="bg-red-600 text-white px-3 py-1 rounded-lg text-sm font-semibold hover:bg-red-700"
                            data-testid="delete-property-btn"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-2">
                        <span className="font-semibold">{property.bhk}</span> | {property.area_sqft} sq ft | {property.floor}
                      </p>
                      <p className="text-2xl font-bold text-brand-500">₹{property.price}L</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Property Modal */}
        {showAddProperty && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 my-8 shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingProperty ? 'Edit Property' : 'Add New Property'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddProperty(false);
                    setEditingProperty(null);
                    setPropertyForm({
                      location: "",
                      society_name: "",
                      bhk: "",
                      area_sqft: "",
                      floor: "",
                      price: "",
                      description: "",
                      contact_whatsapp: "",
                      images: [],
                      video_url: ""
                    });
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleAddProperty} className="space-y-4" data-testid="add-property-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Location *</label>
                    <input
                      type="text"
                      required
                      value={propertyForm.location}
                      onChange={(e) => setPropertyForm({ ...propertyForm, location: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                      data-testid="property-location"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Society Name *</label>
                    <input
                      type="text"
                      required
                      value={propertyForm.society_name}
                      onChange={(e) => setPropertyForm({ ...propertyForm, society_name: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                      data-testid="property-society"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">BHK *</label>
                    <input
                      type="text"
                      required
                      value={propertyForm.bhk}
                      onChange={(e) => setPropertyForm({ ...propertyForm, bhk: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                      placeholder="e.g., 3 BHK"
                      data-testid="property-bhk"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Area (sq ft) *</label>
                    <input
                      type="number"
                      required
                      value={propertyForm.area_sqft}
                      onChange={(e) => setPropertyForm({ ...propertyForm, area_sqft: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                      data-testid="property-area"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Floor *</label>
                    <input
                      type="text"
                      required
                      value={propertyForm.floor}
                      onChange={(e) => setPropertyForm({ ...propertyForm, floor: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                      placeholder="e.g., 3rd Floor"
                      data-testid="property-floor"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Price (Lakhs) *</label>
                    <input
                      type="number"
                      required
                      value={propertyForm.price}
                      onChange={(e) => setPropertyForm({ ...propertyForm, price: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                      placeholder="e.g., 85"
                      data-testid="property-price"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">WhatsApp Number *</label>
                  <input
                    type="text"
                    required
                    value={propertyForm.contact_whatsapp}
                    onChange={(e) => setPropertyForm({ ...propertyForm, contact_whatsapp: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                    placeholder="919999999999"
                    data-testid="property-whatsapp"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
                  <textarea
                    required
                    value={propertyForm.description}
                    onChange={(e) => setPropertyForm({ ...propertyForm, description: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                    rows="3"
                    data-testid="property-description"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Upload Images</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                  />
                  {propertyForm.images.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm text-gray-600">{propertyForm.images.length} image(s) uploaded</p>
                      <div className="grid grid-cols-3 gap-2">
                        {propertyForm.images.map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={img} 
                              alt={`Property ${idx + 1}`} 
                              className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newImages = propertyForm.images.filter((_, i) => i !== idx);
                                setPropertyForm({ ...propertyForm, images: newImages });
                              }}
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Upload Property Video (Optional)</label>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Max file size: <strong>10MB</strong> (MongoDB storage limit). 
                    <br />Supported formats: MP4, MOV, AVI
                    <br />💡 Tip: Compress large videos before uploading
                  </p>
                  {propertyForm.video_url && (
                    <div className="mt-4">
                      <p className="text-sm text-brand-500 font-semibold mb-2">✓ Video uploaded successfully</p>
                      <div className="relative group">
                        <video
                          src={propertyForm.video_url}
                          className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                          controls
                        />
                        <button
                          type="button"
                          onClick={() => setPropertyForm({ ...propertyForm, video_url: "" })}
                          className="absolute top-2 right-2 bg-red-600 text-white rounded-full px-3 py-1 text-sm font-semibold hover:bg-red-700 transition"
                        >
                          Remove Video
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-brand-500 text-white py-3 px-6 rounded-lg font-bold hover:bg-brand-600 transition"
                  data-testid="submit-property"
                >
                  {editingProperty ? 'Update Property' : 'Add Property'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Full Report Modal */}
        {viewingReport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-brand-500 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-2xl font-bold">Property Valuation Report</h2>
                <button
                  onClick={() => setViewingReport(null)}
                  className="text-white hover:bg-brand-600 rounded-full p-2 transition"
                >
                  ✕ Close
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Lead Contact Information */}
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Lead Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-semibold text-gray-900">{viewingReport.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Mobile</p>
                      <p className="font-semibold text-gray-900">{viewingReport.mobile}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold text-gray-900">{viewingReport.email}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-gray-600">Submitted On</p>
                    <p className="font-semibold text-gray-900">{formatISTDateTime(viewingReport.timestamp)}</p>
                  </div>
                </div>

                {/* Property Details Input */}
                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Property Details (User Input)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Location</p>
                      <p className="font-semibold text-gray-900">{viewingReport.valuation_result?.property_details?.location || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Area/Sector</p>
                      <p className="font-semibold text-gray-900">{viewingReport.valuation_result?.property_details?.area_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Society Name</p>
                      <p className="font-semibold text-gray-900">{viewingReport.valuation_result?.property_details?.society_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Property Type</p>
                      <p className="font-semibold text-gray-900">{viewingReport.valuation_result?.property_details?.bhk || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Area (sq ft)</p>
                      <p className="font-semibold text-gray-900">{viewingReport.valuation_result?.property_details?.area_sqft || 'N/A'} sq ft</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Floor</p>
                      <p className="font-semibold text-gray-900">{viewingReport.valuation_result?.property_details?.floor || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Property Age</p>
                      <p className="font-semibold text-gray-900">{viewingReport.valuation_result?.property_details?.age_years || 'N/A'} years</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Position (Facing)</p>
                      <p className="font-semibold text-gray-900">{viewingReport.valuation_result?.property_details?.position || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Furnishing Age</p>
                      <p className="font-semibold text-gray-900">{viewingReport.valuation_result?.property_details?.furnishing_age || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Roof Rights</p>
                      <p className="font-semibold text-gray-900">{viewingReport.valuation_result?.property_details?.has_roof_rights ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>

                {/* Valuation Results */}
                <div className="bg-brand-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Valuation Results</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-4 rounded-lg border-2 border-brand-500">
                      <p className="text-sm text-gray-600 mb-2">Estimated Market Value</p>
                      <p className="text-3xl font-bold text-brand-500">
                        ₹{viewingReport.valuation_result?.estimated_market_value_min || 0}L - ₹{viewingReport.valuation_result?.estimated_market_value_max || 0}L
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border-2 border-green-500">
                      <p className="text-sm text-gray-600 mb-2">Quick Sale Price</p>
                      <p className="text-3xl font-bold text-green-600">
                        ₹{viewingReport.valuation_result?.quick_sale_price || 0}L
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border-2 border-purple-500">
                      <p className="text-sm text-gray-600 mb-2">Investor Safe Buy Price</p>
                      <p className="text-3xl font-bold text-purple-600">
                        ₹{viewingReport.valuation_result?.investor_safe_buy_price || 0}L
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border-2 border-orange-500">
                      <p className="text-sm text-gray-600 mb-2">Price per Sq Ft</p>
                      <p className="text-3xl font-bold text-orange-600">
                        ₹{viewingReport.valuation_result?.price_per_sqft || 0}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 bg-white p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">Demand Score</p>
                    <span className={`px-4 py-2 rounded-full text-lg font-bold ${
                      viewingReport.valuation_result?.demand_score === 'High' ? 'bg-brand-100 text-brand-500' :
                      viewingReport.valuation_result?.demand_score === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {viewingReport.valuation_result?.demand_score || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 p-6 rounded-b-2xl">
                <button
                  onClick={() => setViewingReport(null)}
                  className="w-full bg-brand-500 text-white py-3 px-6 rounded-lg font-bold hover:bg-brand-600 transition"
                >
                  Close Report
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// ==================== BUYER STRATEGY PAGE ====================

export default AdminDashboard;
